import { withFormik } from "formik";
import * as yup from 'yup';
import SparkLocationsForm, { LocationSubStep, ManageSparkLocationsFormData, ManageSparkLocationsFormProps } from "./SparkLocationsForm";
import { createEmptyApplication, getFeatureFlags, toggleAllowsTemplating, updateApplication, updateApplicationFeatureFlagSettings } from "../../../../../shared/api/controllers/applications";
import { addAppliedApplicationTemplate, getAppliedApplicationTemplatesForApp, updateAppliedApplicationTemplate } from "../../../../../shared/api/controllers/appliedApplicationTemplates";
import UpdateAppliedApplicationTemplateFormRequest from "../../../../../shared/models/templating/api/UpdateAppliedApplicationTemplateFormRequest";
import { createTemplateConfiguration, getAvailableTemplateConfigurations, getOrganizationTemplateConfigurations, updateTemplateConfiguration } from "../../../../../shared/api/controllers/templateConfigurations";
import TemplateConfigurationModel from "../../../../../shared/models/templating/api/TemplateConfigurationModel";
import TemplateFormSectionModel from "../../../../../shared/models/templating/api/TemplateFormSectionModel";
import { v4 as uuidv4 } from 'uuid';
import { getAIAnsweringModuleId, getFoodOrderingModuleId, getHealthcareSchedulingModuleId } from "../../../../../shared/constants/ModuleConsts";

import TemplateFormFieldValueModel from "../../../../../shared/models/templating/api/TemplateFormFieldValueModel";
import { AIAnsweringFeatureFlagId, FoodOrderingFeatureFlagId, GenAIFoodOrdering, HealthcareSchedulingFeatureFlagId, OutboundCallFeatureFlagId } from "../../../../../shared/constants/featureFlagIds";
import UpdateApplicationRequest from "../../../../../shared/models/applications/api/UpdateApplicationRequest";
import { aiAnsweringCommonAppName, aiAnsweringCustomModuleName, aiDentalSchedulingCommonAppName, aiDentalSchedulingCustomModuleName, aiOrderingCommonAppName, aiOrderingCustomModuleName } from "../../../../../shared/constants/sparkConstants/moduleConsts";
import { getDefaultAssistantConfig } from "../../../../../shared/constants/sparkConstants/defaultSparkAssistantConfigurations";
import { FoodOrderingGenAIModelSettingId, FoodOrderingGenAIPromptOverrideSettingId } from "../../../../../shared/constants/featureFlagSettingIds";
import { initiateMenuUpdate, syncMenus } from "../../../../../shared/api/controllers/voicifyMenuItems";
import UpdateMenuRequest from "../../../../../shared/models/nlp/menu/UpdateMenuRequest";
import { SyncMenusRequest } from "../../../../../shared/models/nlp/menu/SyncMenusRequest";
import { POSNames } from "../../../../../shared/models/nlp/PosNames";
import { getApplicationEnvironments } from "../../../../../shared/api/controllers/applicationEnvironment";
import { updateEnvironmentSettings } from "../../../../../shared/api/controllers/environments";
import TemplateFormFieldModel from "../../../../../shared/models/templating/api/TemplateFormFieldModel";
import AppliedApplicationTemplateModel from "../../../../../shared/models/templating/api/AppliedApplicationTemplateModel";
import { dedupeFieldValuesForField, fieldsMatch, findFieldValueForField, getFieldIdentity } from "../../../../../shared/utils/templateFieldMatching";

export { dedupeFieldValuesForField, fieldsMatch, findFieldValueForField, getFieldIdentity };

export function getAppliedTemplateConfigurationId(appliedTemplate: AppliedApplicationTemplateModel | any): string | undefined {
    return appliedTemplate?.templateForm?.templateConfigurationId
        || appliedTemplate?.templateForm?.templateConfiguration?.id;
}

export function isOrgOwnedAppliedTemplate(appliedTemplate: AppliedApplicationTemplateModel | any, organizationId: string): boolean {
    return appliedTemplate?.templateForm?.templateConfiguration?.organizationId === organizationId;
}

export function getManagedLocationAppliedTemplate(appliedTemplates: AppliedApplicationTemplateModel[] = [], organizationId: string, preferredTemplateConfigurationId?: string): AppliedApplicationTemplateModel | undefined {
    return appliedTemplates.find(appliedTemplate =>
        preferredTemplateConfigurationId
        && getAppliedTemplateConfigurationId(appliedTemplate) === preferredTemplateConfigurationId
        && isOrgOwnedAppliedTemplate(appliedTemplate, organizationId))
        || appliedTemplates.find(appliedTemplate => isOrgOwnedAppliedTemplate(appliedTemplate, organizationId));
}

export function hasDirectCoreAppliedTemplate(appliedTemplates: AppliedApplicationTemplateModel[] = [], coreModuleIds: string[] = []): boolean {
    return appliedTemplates.some(appliedTemplate => {
        const templateConfigurationId = getAppliedTemplateConfigurationId(appliedTemplate);
        return !!templateConfigurationId && coreModuleIds.some(coreModuleId => coreModuleId === templateConfigurationId);
    });
}

/**
 * Resolves the value to persist for a single template form field when updating an existing
 * location app's applied template.
 *
 * Priority:
 *  1. Deselected field  → reset to schema default (field moved back to shared config)
 *  2. Selected field with an existing saved value → preserve the existing value
 *  3. Selected field with no existing saved value (newly added) → seed from defaultConfig
 *  4. Non-selected field with an existing saved value → preserve the existing value
 *  5. Fallback → schema default value
 */
export function resolveLocationFieldValue(params: {
    formField: TemplateFormFieldModel;
    isFieldSelected: boolean;
    isFieldDeselected: boolean;
    existingValue: string | undefined | null;
    defaultConfig: Record<string, any>;
}): string | undefined {
    const { formField, isFieldSelected, isFieldDeselected, existingValue, defaultConfig } = params;

    if (isFieldDeselected) {
        return formField.defaultValue;
    }

    if (isFieldSelected) {
        if (existingValue !== undefined && existingValue !== null) {
            return existingValue;
        }
        // Newly added field — seed with default config value
        const configKey =
            formField.associatedVariable && defaultConfig[formField.associatedVariable] !== undefined
                ? formField.associatedVariable
                : formField.title;
        if (defaultConfig[configKey] !== undefined) {
            const configValue = defaultConfig[configKey];
            if (configValue !== '' && configValue !== null && configValue !== undefined) {
                return typeof configValue === 'string' ? configValue : String(configValue);
            }
        }
        return formField.defaultValue;
    }

    // Field is not location-specific — preserve existing saved value if present
    if (existingValue !== undefined && existingValue !== null) {
        return existingValue;
    }

    return formField.defaultValue;
}

/**
 * Merges an outbound feature flag into an existing feature flags array without
 * replacing any other flags.  Any prior outbound entry is removed first so the
 * result always contains exactly one entry for that flag.
 *
 * Used in `updateExistingLocationApp` to avoid a full-replace PUT from silently
 * dropping flags that this form does not manage.
 */
export function mergeOutboundFeatureFlag(
    existingFlags: { id: string; name?: string; featureFlagSettings?: any[] }[],
    outboundFlagId: string,
    outboundFlag: { id?: string; name?: string; featureFlagSettings?: any[] }
): { id: string; name?: string; featureFlagSettings?: any[] }[] {
    return [
        ...existingFlags.filter(f => f.id !== outboundFlagId),
        {
            id: outboundFlagId,
            name: outboundFlag.name,
            featureFlagSettings: outboundFlag.featureFlagSettings
        }
    ];
}

const ManageSparkLocationsForm = withFormik<ManageSparkLocationsFormProps, ManageSparkLocationsFormData>({
    mapPropsToValues: props => (
        {
            commonAppName: aiAnsweringCommonAppName,
            locationNames: [""],
            selectedFormFields: props.locationAppFormFields ? props.locationAppFormFields : [],
            deselectedFormFields: [],
            assistantType: "",
            assistantSetupType: "",
            pmsName: "",
            posNameOrdering: "",
            storeIds: {},
            callDirections: {},
        }
    ),
    validationContext: (props) => ({ isNewOrg: !props.commonApp, locationApps: props.locationApps }),
    validationSchema: yup.object().shape(
        {
            locationNames: yup
                .array()
                .of(yup.string().nullable().optional())
                .test(
                    'at-least-one-location',
                    'At least one location is required',
                    (val) => Array.isArray(val) && val.some(name => name && name.trim().length > 0)
                ),
            assistantType: yup.string().required('Assistant type is required'),
            assistantSetupType: yup.string().when('$isNewOrg', {
                is: true,
                then: (schema) => schema.required('Assistant setup type is required'),
                otherwise: (schema) => schema.notRequired()
            }),
            pmsName: yup.string().when(['assistantType', '$isNewOrg'], {
                is: (assistantType: string, isNewOrg: boolean) => assistantType === 'dental-scheduling' && isNewOrg,
                then: (schema) => schema.required('PMS Name is required for Dental Scheduling'),
                otherwise: (schema) => schema.notRequired()
            }),
            posNameOrdering: yup.string().when(['assistantType', '$isNewOrg'], {
                is: (assistantType: string, isNewOrg: boolean) => assistantType === 'ai-ordering' && isNewOrg,
                then: (schema) => schema.required('POS Name is required for AI Ordering'),
                otherwise: (schema) => schema.notRequired()
            }),
            storeIds: yup.mixed().notRequired()
        }
    ),
    handleSubmit: async (values, { props, setStatus }) => {

        // Track what actually happened for the success message
        const commonAppWasCreated = !props.commonApp;
        const createdNames: string[] = [];
        const updatedNames: string[] = [];

        props.setIsSubmittingForm(true);
        props.setProgressMode?.(commonAppWasCreated ? 'creating' : 'updating');
        props.setProgressStep?.('setup-module');

        const getCommonAppNameForAssistantType = (assistantType: string) => {
            switch (assistantType) {
                case 'ai-ordering': return aiOrderingCommonAppName;
                case 'dental-scheduling': return aiDentalSchedulingCommonAppName;
                default: return aiAnsweringCommonAppName;
            }
        };
        props.setProgressLocationName?.(getCommonAppNameForAssistantType(values.assistantType));

        // Clear any previous errors
        setStatus(null);

        try {
            const aiAnsweringTemplateConfigId = getAIAnsweringModuleId();
            const foodOrderingModuleId = getFoodOrderingModuleId();
            const healthcareSchedulingModuleId = getHealthcareSchedulingModuleId();
            let commonAppId = "";
            let commonAppTemplateConfigId = "";

            const getSelectedModuleId = (assistantType: string) => {
                switch (assistantType) {
                    case 'ai-answering':
                        return aiAnsweringTemplateConfigId;
                    case 'ai-ordering':
                        return foodOrderingModuleId;
                    case 'dental-scheduling':
                        return healthcareSchedulingModuleId;
                    default:
                        return aiAnsweringTemplateConfigId;
                }
            };

            const getFeatureFlagForAssistantType = (assistantType: string) => {
                switch (assistantType) {
                    case 'ai-answering':
                        return AIAnsweringFeatureFlagId;
                    case 'ai-ordering':
                        return FoodOrderingFeatureFlagId;
                    case 'dental-scheduling':
                        return HealthcareSchedulingFeatureFlagId;
                    default:
                        return AIAnsweringFeatureFlagId;
                }
            };

            const getCustomModuleName = (assistantType: string) => {
                switch (assistantType) {
                    case 'ai-answering':
                        return aiAnsweringCustomModuleName;
                    case 'ai-ordering':
                        return aiOrderingCustomModuleName;
                    case 'dental-scheduling':
                        return aiDentalSchedulingCustomModuleName;
                    default:
                        return aiAnsweringCustomModuleName;
                }
            };

            const updateTemplateFormSection = (orgTemplateConfig: TemplateConfigurationModel, coreTemplateConfig: TemplateConfigurationModel, values: ManageSparkLocationsFormData) => {

                const updatedSections: any[] = [];
                for (const commonAppSection of [...coreTemplateConfig.templateForm.templateFormSections]) {

                    if (commonAppSection.templateFormFields.some((field: any) => values.selectedFormFields.some((selectedField: any) => fieldsMatch(selectedField, field)))) {
                        let newSection = orgTemplateConfig.templateForm.templateFormSections.find((templateSection: any) => templateSection.title === commonAppSection.title);

                        if (!newSection) {
                            newSection = {
                                ...commonAppSection,
                                templateFormFields: [],
                                templateFormId: orgTemplateConfig.templateForm.id,
                                id: uuidv4()
                            };
                        } else {
                            newSection =
                            {
                                ...commonAppSection,
                                templateFormFields: newSection.templateFormFields,
                                id: newSection.id,
                                templateFormId: newSection.templateFormId,
                            }
                        };

                        const updatedFields: any[] = [];

                        for (const commonAppField of commonAppSection.templateFormFields) {
                            if (values.selectedFormFields.some((selectedField: any) => fieldsMatch(selectedField, commonAppField))) {
                                let newField = newSection.templateFormFields.find((templateField: any) => fieldsMatch(templateField, commonAppField));
                                if (!newField) {
                                    newField = {
                                        ...commonAppField,
                                        templateFormSectionId: newSection.id,
                                        id: uuidv4()
                                    };
                                } else {
                                    newField = {
                                        ...commonAppField,
                                        id: newField.id,
                                        templateFormSectionId: newSection.id,
                                    }
                                }
                                updatedFields.push(newField);
                            }
                        };

                        newSection.templateFormFields = updatedFields;
                        updatedSections.push(newSection);

                    }
                }

                return updatedSections;
            };

            const updateTemplateFormFieldValues = (formFieldValues: TemplateFormFieldValueModel[], commonAppSections: TemplateFormSectionModel[], isUpdate?: boolean, applyDefaultsForCommonApp?: boolean) => {
                const templateValues = [];

                // Get default configuration if we're applying defaults for common app
                const defaultConfig = applyDefaultsForCommonApp
                    ? getDefaultAssistantConfig(values.assistantType, values.pmsName || values.posNameOrdering, '', '')
                    : null;

                for (const section of commonAppSections) {

                    section.templateFormFields.forEach((formField) => {

                        let formFieldValueModel = findFieldValueForField(formField, formFieldValues);
                        let formFieldValue = formField.defaultValue;

                        if (formFieldValueModel) {
                            formFieldValue = formFieldValueModel.value;
                        };

                        if (values.selectedFormFields.some(sf => fieldsMatch(sf, formField)) && formField.associatedVariable) {
                            formFieldValue = formField.associatedVariable;
                        };

                        // Apply default config values for fields NOT selected as location-specific
                        if (applyDefaultsForCommonApp && defaultConfig) {
                            const isFieldSelected = values.selectedFormFields.some(
                                sf => fieldsMatch(sf, formField)
                            );

                            // Look up by associatedVariable first (stable identifier), fall back to title
                            const configKey = formField.associatedVariable && defaultConfig[formField.associatedVariable] !== undefined
                                ? formField.associatedVariable
                                : formField.title;

                            // Only apply to non-selected fields
                            if (!isFieldSelected && defaultConfig[configKey] !== undefined) {
                                const configValue = defaultConfig[configKey];
                                if (configValue !== '' && configValue !== null && configValue !== undefined) {
                                    formFieldValue = typeof configValue === 'string' ? configValue : String(configValue);
                                }
                            }
                        }

                        if (isUpdate) {
                            if (values.deselectedFormFields.some(df => fieldsMatch(df, formField))) {
                                if (formField.defaultValue) {
                                    formFieldValue = formField.defaultValue;
                                } else {
                                    formFieldValue = undefined;
                                }
                            };
                        };

                        const fieldValue = {
                            value: formFieldValue,
                            fieldType: formField.fieldType,
                            tip: formField.tip,
                            title: formField.title,
                            label: formField.label,
                            defaultValue: formField.defaultValue,
                            associatedVariable: formField.associatedVariable,
                            placeholder: formField.placeholder,
                            templateFormFieldId: formField.id
                        };

                        templateValues.push(fieldValue);

                    });
                }

                return templateValues;

            };

            const createCommonAppAndAppliedAppTemplate = async () => {

                // Get the appropriate common app name based on assistant type
                const getCommonAppName = (assistantType: string) => {
                    switch (assistantType) {
                        case 'ai-answering':
                            return aiAnsweringCommonAppName;
                        case 'ai-ordering':
                            return aiOrderingCommonAppName;
                        case 'dental-scheduling':
                            return aiDentalSchedulingCommonAppName;
                        default:
                            return aiAnsweringCommonAppName;
                    }
                };

                const commonAppName = getCommonAppName(values.assistantType);

                // create common app
                const createCommonAppResponse = await createEmptyApplication(props.orgContainer.state.currentOrganization.id, {
                    name: commonAppName,
                    shortDescription: commonAppName,
                    description: commonAppName,
                    invocationPhrase: commonAppName
                });

                if (createCommonAppResponse.resultType !== 'Ok' || !createCommonAppResponse.data?.id) {
                    throw new Error(`Failed to create shared configuration app: ${createCommonAppResponse.errors?.join(', ') || createCommonAppResponse.resultType}`);
                }

                commonAppId = createCommonAppResponse.data.id

                // update common app to allow templating
                await toggleAllowsTemplating(createCommonAppResponse.data.id, true);

                // Get the selected module ID based on assistant type
                const selectedModuleId = getSelectedModuleId(values.assistantType);

                // add applied app template referencing the selected template configuration to common app
                const applySelectedTemplateToCommonAppResponse = await addAppliedApplicationTemplate(commonAppId, selectedModuleId);

                if (applySelectedTemplateToCommonAppResponse.resultType !== 'Ok' || !applySelectedTemplateToCommonAppResponse.data) {
                    throw new Error(`Failed to apply template to shared configuration app: ${applySelectedTemplateToCommonAppResponse.errors?.join(', ') || applySelectedTemplateToCommonAppResponse.resultType}`);
                }

                // Get the template sections for the selected assistant type
                const availableTemplateConfigsResponse = await getAvailableTemplateConfigurations(props.orgContainer.state.currentOrganization.id);
                if (availableTemplateConfigsResponse.resultType !== 'Ok' || !availableTemplateConfigsResponse.data) {
                    throw new Error(`Failed to load available template configurations: ${availableTemplateConfigsResponse.resultType}`);
                }
                const selectedModuleConfig = availableTemplateConfigsResponse.data.find(config => config.id === selectedModuleId);
                const selectedModuleSections = selectedModuleConfig ? selectedModuleConfig.templateForm.templateFormSections : [];

                // update the common app applied application template with the latest template form field values
                const updatedCommonAppAppliedTemplateRequest: UpdateAppliedApplicationTemplateFormRequest = {
                    templateFormFieldValues: updateTemplateFormFieldValues(applySelectedTemplateToCommonAppResponse.data.templateFormFieldValues, selectedModuleSections, false, true),
                    dynamicFormSections: applySelectedTemplateToCommonAppResponse.data.dynamicFormSections,
                    name: applySelectedTemplateToCommonAppResponse.data.name,
                };

                const commonAppAppliedAppId = applySelectedTemplateToCommonAppResponse.data.id;

                await updateAppliedApplicationTemplate(commonAppAppliedAppId, updatedCommonAppAppliedTemplateRequest);

                const updateApplicationRequest: UpdateApplicationRequest = {
                    name: createCommonAppResponse.data.name,
                    shortDescription: createCommonAppResponse.data.shortDescription,
                    description: createCommonAppResponse.data.description,
                    keywords: createCommonAppResponse.data.keywords,
                    invocationPhrase: createCommonAppResponse.data.invocationPhrase,
                    imageItemId: createCommonAppResponse.data.imageItemId,
                    featureFlags: [{ id: getFeatureFlagForAssistantType(values.assistantType) }],
                    ...(values.assistantType === 'dental-scheduling' && {
                        pmsName: values.pmsName
                    })
                };

                await updateApplication(commonAppId, updateApplicationRequest);

            };

            const updateCommonAppAndAppliedAppTemplate = async () => {

                commonAppId = props.commonApp.id;

                // Get the template sections for the selected assistant type
                const availableTemplateConfigsResponse = await getAvailableTemplateConfigurations(props.orgContainer.state.currentOrganization.id);
                if (availableTemplateConfigsResponse.resultType !== 'Ok' || !availableTemplateConfigsResponse.data) {
                    throw new Error(`Failed to load available template configurations: ${availableTemplateConfigsResponse.resultType}`);
                }
                const selectedModuleId = getSelectedModuleId(values.assistantType);
                const selectedModuleConfig = availableTemplateConfigsResponse.data.find(config => config.id === selectedModuleId);
                const selectedModuleSections = selectedModuleConfig ? selectedModuleConfig.templateForm.templateFormSections : [];

                // update the common app applied application template with the latest configuration
                const appliedAppTemplatesResponse = await getAppliedApplicationTemplatesForApp(commonAppId);
                if (appliedAppTemplatesResponse.resultType !== 'Ok' || !appliedAppTemplatesResponse.data) {
                    throw new Error(`Failed to load applied templates for shared configuration app: ${appliedAppTemplatesResponse.resultType}`);
                }
                for (let i = 0; i < appliedAppTemplatesResponse.data?.length; i++) {
                    const appliedApplicationTemplateIndex = i;
                    const commonAppAppliedAppTemplate = appliedAppTemplatesResponse.data[appliedApplicationTemplateIndex]
                    const commonAppAppliedAppTemplateId = commonAppAppliedAppTemplate.id;

                    const updateAppliedAppTemplateRequest: UpdateAppliedApplicationTemplateFormRequest = {
                        templateFormFieldValues: updateTemplateFormFieldValues(commonAppAppliedAppTemplate.templateFormFieldValues, selectedModuleSections, true),
                        dynamicFormSections: appliedAppTemplatesResponse.data[appliedApplicationTemplateIndex].dynamicFormSections,
                        name: appliedAppTemplatesResponse.data[appliedApplicationTemplateIndex].name,
                    };

                    await updateAppliedApplicationTemplate(commonAppAppliedAppTemplateId, updateAppliedAppTemplateRequest);

                }
            };

            const createCommonAppTemplateConfiguration = async () => {

                const commonAppTemplateFormId = uuidv4();
                const customModuleName = getCustomModuleName(values.assistantType);

                // Get the template sections for the selected assistant type
                const availableTemplateConfigsResponse = await getAvailableTemplateConfigurations(props.orgContainer.state.currentOrganization.id);
                const selectedModuleId = getSelectedModuleId(values.assistantType);
                const selectedModuleConfig = availableTemplateConfigsResponse.data.find(config => config.id === selectedModuleId);

                if (!selectedModuleConfig) {
                    throw new Error(`Template configuration not found for assistant type "${values.assistantType}". This module may not be configured yet. Please contact support if you expected this module to be available.`);
                }

                const selectedModuleSections = selectedModuleConfig.templateForm.templateFormSections;

                const commonAppTemplateConfigurationRequest: TemplateConfigurationModel = {
                    name: customModuleName,
                    description: `${props.orgContainer.state.currentOrganization.name} ${customModuleName}`,
                    organizationId: props.orgContainer.state.currentOrganization.id,
                    templateApplicationId: commonAppId,
                    finalizeFormUrl: null,
                    templateForm: {
                        id: commonAppTemplateFormId,
                        templateFormSections: selectedModuleSections.filter(section => {
                            section.templateFormFields = section.templateFormFields.filter(field => {
                                return values.selectedFormFields.some(selectedField =>
                                    fieldsMatch(selectedField, field)
                                );
                            });
                            return section.templateFormFields.length > 0;
                        })
                    },
                    currentVersion: { "versionName": "1.0", "releaseNotes": "initial template" }
                };

                for (const commonAppTemplateFormSection of commonAppTemplateConfigurationRequest.templateForm.templateFormSections) {
                    commonAppTemplateFormSection.templateFormId = commonAppTemplateFormId;
                    commonAppTemplateFormSection.id = uuidv4();
                    for (const commonAppTemplateFormField of commonAppTemplateFormSection.templateFormFields) {
                        commonAppTemplateFormField.id = uuidv4();
                        commonAppTemplateFormField.templateFormSectionId = commonAppTemplateFormSection.id;
                    }
                }

                const createTemplateConfigurationResponse = await createTemplateConfiguration(commonAppTemplateConfigurationRequest);
                commonAppTemplateConfigId = createTemplateConfigurationResponse.data.id;

            };

            const updateCommonAppTemplateConfiguration = async () => {

                commonAppTemplateConfigId = orgTemplateConfig.id;

                const availableTemplateConfigsResponse = await getAvailableTemplateConfigurations(props.orgContainer.state.currentOrganization.id);

                // Get the selected module ID based on assistant type
                const selectedModuleId = getSelectedModuleId(values.assistantType);

                const coreTemplateConfig = availableTemplateConfigsResponse.data.find(templateConfig => templateConfig.id === selectedModuleId);

                // Ensure we have a valid core template config before proceeding
                if (!coreTemplateConfig) {
                    throw new Error(`Template configuration not found for assistant type "${values.assistantType}". This module may not be configured yet. Please contact support if you expected this module to be available.`);
                }

                // No longer automatically merging all modules - only use the selected one
                const commonAppUpdatedTemplateForm = {
                    ...orgTemplateConfig.templateForm,
                    templateFormSections: updateTemplateFormSection(orgTemplateConfig, coreTemplateConfig, values)
                }

                const commonAppUpdatedTemplateConfiguration: TemplateConfigurationModel = {
                    ...orgTemplateConfig,
                    templateForm: commonAppUpdatedTemplateForm
                }

                // update common app template configuration with latest form from selected module
                await updateTemplateConfiguration(commonAppTemplateConfigId, commonAppUpdatedTemplateConfiguration);
            };

            const createLocationAppAndApplyAppTemplate = async (locationName: string, locationIndex: number) => {

                props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'creating-app' as LocationSubStep }));

                const createLocationAppResponse = await createEmptyApplication(props.orgContainer.state.currentOrganization.id, {
                    name: locationName,
                    shortDescription: locationName,
                    description: locationName,
                    invocationPhrase: locationName
                });
                if (createLocationAppResponse.resultType !== 'Ok' || !createLocationAppResponse.data?.id) {
                    throw new Error(`Failed to create location app for "${locationName}": ${createLocationAppResponse.errors?.join(', ') || createLocationAppResponse.resultType}`);
                }

                const locationAppId = createLocationAppResponse.data.id;
                const locationAppSecret = createLocationAppResponse.data.secret ?? "";
                const locationAppFullName = locationName;

                // Fetch all feature flags once — shared by Manage Menu and location app GenAI steps
                let genAIFoodOrderingFlag: any = undefined;
                if (values.assistantType === 'ai-ordering') {
                    try {
                        const allFeatureFlagsResponse = await getFeatureFlags();
                        genAIFoodOrderingFlag = allFeatureFlagsResponse.data?.find(f => f.id === GenAIFoodOrdering);
                    } catch (e) {
                        // GenAI settings will not be applied if feature flags cannot be fetched
                    }
                }

                const buildGenAISettings = (flag: any) =>
                    (flag.featureFlagSettings ?? []).map((setting: any) => {
                        if (setting.id === FoodOrderingGenAIModelSettingId) {
                            return { ...setting, value: 'gemini-2.5-flash' };
                        }
                        if (setting.id === FoodOrderingGenAIPromptOverrideSettingId) {
                            return { ...setting, value: '{{constant.base_ordering_prompt}}' };
                        }
                        if (setting.name === 'Generative Assistant Ordering') {
                            return { ...setting, value: 'true' };
                        }
                        if (setting.name === 'Disable Legacy Checkout Flow') {
                            return { ...setting, value: 'true' };
                        }
                        return setting;
                    });

                // ----------------------------------------------------------------
                // AI Ordering: create Manage Menu app + apply GenAI settings
                // ----------------------------------------------------------------
                let manageMenuAppId: string | undefined;
                if (values.assistantType === 'ai-ordering') {
                    props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'create-manage-menu' as LocationSubStep }));
                    const storeId = values.storeIds?.[locationIndex] ?? '';
                    const manageMenuAppName = `${locationAppFullName} - Manage Menu`;

                    // Step A-1: Create Manage Menu app
                    const manageMenuAppResponse = await createEmptyApplication(props.orgContainer.state.currentOrganization.id, {
                        name: manageMenuAppName,
                        shortDescription: manageMenuAppName,
                        description: manageMenuAppName,
                        invocationPhrase: manageMenuAppName
                    });
                    if (manageMenuAppResponse.resultType !== 'Ok' || !manageMenuAppResponse.data?.id) {
                        throw new Error(`Failed to create Manage Menu app for "${locationName}": ${manageMenuAppResponse.errors?.join(', ') || manageMenuAppResponse.resultType}`);
                    }
                    manageMenuAppId = manageMenuAppResponse.data.id;

                    // Step A-2: Apply Food Ordering flag + POS settings to Manage Menu app
                    const manageMenuUpdateRequest: UpdateApplicationRequest = {
                        name: manageMenuAppResponse.data.name,
                        shortDescription: manageMenuAppResponse.data.shortDescription,
                        description: manageMenuAppResponse.data.description,
                        keywords: manageMenuAppResponse.data.keywords,
                        invocationPhrase: manageMenuAppResponse.data.invocationPhrase,
                        imageItemId: manageMenuAppResponse.data.imageItemId,
                        featureFlags: [{ id: FoodOrderingFeatureFlagId }],
                        posName: values.posNameOrdering,
                        posLocationId: storeId
                    };
                    await updateApplication(manageMenuAppId, manageMenuUpdateRequest);

                    // Step A-3: Apply GenAI settings to Manage Menu app (non-fatal if flag not found)
                    if (genAIFoodOrderingFlag) {
                        await updateApplicationFeatureFlagSettings(manageMenuAppId, {
                            featureFlagId: GenAIFoodOrdering,
                            featureFlagSettings: buildGenAISettings(genAIFoodOrderingFlag)
                        });
                    }
                }

                // ----------------------------------------------------------------
                // Standard location app update
                // ----------------------------------------------------------------
                const storeId = values.assistantType === 'ai-ordering'
                    ? (values.storeIds?.[locationIndex] ?? '')
                    : undefined;

                const callDirection = values.callDirections?.[locationIndex] ?? 'inbound';
                const hasOutbound = callDirection === 'outbound' || callDirection === 'inbound-outbound';

                // Fetch outbound feature flag with settings for dental scheduling with outbound
                let outboundFeatureFlag: any = undefined;
                if (values.assistantType === 'dental-scheduling' && hasOutbound) {
                    try {
                        const allFeatureFlagsResponse = await getFeatureFlags();
                        outboundFeatureFlag = allFeatureFlagsResponse.data?.find((f: any) => f.id === OutboundCallFeatureFlagId);
                    } catch (e) {
                        // Outbound settings will not be applied if feature flags cannot be fetched
                    }
                }

                const locationAppFeatureFlags = values.assistantType === 'ai-ordering'
                    ? [{ id: FoodOrderingFeatureFlagId }, { id: GenAIFoodOrdering }]
                    : values.assistantType === 'dental-scheduling'
                        ? (() => {
                            const flags: { id: string; name?: string; featureFlagSettings?: any[] }[] = [{ id: getFeatureFlagForAssistantType(values.assistantType) }];
                            if (outboundFeatureFlag) {
                                flags.push({
                                    id: OutboundCallFeatureFlagId,
                                    name: outboundFeatureFlag.name,
                                    featureFlagSettings: outboundFeatureFlag.featureFlagSettings
                                });
                            }
                            return flags;
                        })()
                        : [{ id: getFeatureFlagForAssistantType(values.assistantType) }];

                const updateApplicationRequest: UpdateApplicationRequest = {
                    name: createLocationAppResponse.data.name,
                    shortDescription: createLocationAppResponse.data.shortDescription,
                    description: createLocationAppResponse.data.description,
                    keywords: createLocationAppResponse.data.keywords,
                    invocationPhrase: createLocationAppResponse.data.invocationPhrase,
                    imageItemId: createLocationAppResponse.data.imageItemId,
                    featureFlags: locationAppFeatureFlags,
                    ...(values.assistantType === 'ai-ordering' && {
                        posName: values.posNameOrdering,
                        posLocationId: storeId,
                        manageMenuApplicationId: manageMenuAppId
                    }),
                    ...(values.assistantType === 'dental-scheduling' && {
                        pmsName: values.pmsName,
                        pmsLocationId: values.storeIds?.[locationIndex] ?? undefined,
                        ...(hasOutbound && {
                            OutboundCallIntegrationName: values.pmsName,
                            OutboundCallLocationId: values.storeIds?.[locationIndex] || ""
                        })
                    })
                };

                await updateApplication(locationAppId, updateApplicationRequest);

                // Update ApplicationEnvironment with outbound feature flag if needed
                if (values.assistantType === 'dental-scheduling' && hasOutbound && outboundFeatureFlag) {
                    const environmentsResponse = await getApplicationEnvironments(locationAppId);
                    const productionEnv = environmentsResponse.data?.find((env: any) => env.name === 'Production');
                    if (productionEnv) {
                        await updateEnvironmentSettings(productionEnv.id, {
                            name: productionEnv.name,
                            applicationName: createLocationAppResponse.data.name,
                            featureFlags: [{
                                id: OutboundCallFeatureFlagId,
                                name: outboundFeatureFlag.name,
                                featureFlagSettings: outboundFeatureFlag.featureFlagSettings
                            }]
                        });
                    }
                }

                // Apply GenAI settings to location app (non-fatal if flag not found)
                if (values.assistantType === 'ai-ordering' && genAIFoodOrderingFlag) {
                    await updateApplicationFeatureFlagSettings(locationAppId, {
                        featureFlagId: GenAIFoodOrdering,
                        featureFlagSettings: buildGenAISettings(genAIFoodOrderingFlag)
                    });
                }

                // Enable detailed analytics for the new app
                await props.appContainer.updateDetailedAnalytics(locationAppId, true);

                props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'applying-template' as LocationSubStep }));

                // Apply the template configuration to the location app
                const appliedTemplateResponse = await addAppliedApplicationTemplate(locationAppId, commonAppTemplateConfigId);

                // Get the org-scoped template configuration to access the location-specific form fields
                const orgTemplateConfigsForLocation = await getOrganizationTemplateConfigurations(props.orgContainer.state.currentOrganization.id);
                const orgTemplateConfigForLocation = orgTemplateConfigsForLocation.data?.find(config => config.id === commonAppTemplateConfigId);

                // Get default configuration for this assistant type
                const defaultConfig = getDefaultAssistantConfig(
                    values.assistantType,
                    values.pmsName || values.posNameOrdering,
                    locationName,
                    locationAppId,
                    locationAppSecret,
                    values.assistantType === 'ai-ordering' ? (values.storeIds?.[locationIndex] ?? '') : undefined
                );

                // Build template field values from the template configuration sections
                const templateFieldValues: TemplateFormFieldValueModel[] = [];

                if (orgTemplateConfigForLocation) {
                    for (const section of orgTemplateConfigForLocation.templateForm.templateFormSections) {
                        for (const formField of section.templateFormFields) {
                            const fieldTitle = formField.title;
                            let fieldValue = formField.defaultValue;

                            // Check if this field is selected as location-specific
                            const isFieldSelected = values.selectedFormFields.some(
                                selectedField => fieldsMatch(selectedField, formField)
                            );

                            // Look up by associatedVariable first (stable identifier), fall back to title
                            const configKey = formField.associatedVariable && defaultConfig[formField.associatedVariable] !== undefined
                                ? formField.associatedVariable
                                : fieldTitle;

                            // Apply default config value if field is selected and has a default
                            if (isFieldSelected && defaultConfig[configKey] !== undefined) {
                                const configValue = defaultConfig[configKey];
                                if (configValue !== '' && configValue !== null && configValue !== undefined) {
                                    fieldValue = typeof configValue === 'string' ? configValue : String(configValue);
                                }
                            }

                            // Create the field value model
                            templateFieldValues.push({
                                value: fieldValue,
                                fieldType: formField.fieldType,
                                tip: formField.tip,
                                title: formField.title,
                                label: formField.label,
                                defaultValue: formField.defaultValue,
                                associatedVariable: formField.associatedVariable,
                                placeholder: formField.placeholder,
                                templateFormFieldId: formField.id
                            });
                        }
                    }
                }

                // Update the applied template with location-specific values
                const updateLocationAppTemplateRequest: UpdateAppliedApplicationTemplateFormRequest = {
                    templateFormFieldValues: templateFieldValues,
                    dynamicFormSections: appliedTemplateResponse.data.dynamicFormSections,
                    name: appliedTemplateResponse.data.name,
                };

                await updateAppliedApplicationTemplate(appliedTemplateResponse.data.id, updateLocationAppTemplateRequest);

                // ----------------------------------------------------------------
                // AI Ordering: Pull Latest Menu from POS, then Train and Publish
                // ----------------------------------------------------------------
                if (values.assistantType === 'ai-ordering' && manageMenuAppId) {
                    const locationStoreId = values.storeIds?.[locationIndex] ?? '';

                    // Step 1: Pull Latest Menu from POS (updates the manage menu app only)
                    props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'pull-menu' as LocationSubStep }));
                    const pullMenuRequest: UpdateMenuRequest = {
                        locationId: locationStoreId,
                        posName: values.posNameOrdering as POSNames,
                        manageMenuApplicationId: manageMenuAppId,
                        liveApplicationId: locationAppId,
                        updateLiveApplication: false,
                        skipEntityExapnsion: true,
                        skipMenuAugmentation: true,
                        orgId: props.orgContainer.state.currentOrganization.id,
                    };
                    await initiateMenuUpdate(pullMenuRequest);

                    // Step 2: Train and Publish (moves active items from manage menu app to location/assistant app)
                    props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'sync-module' as LocationSubStep }));
                    const syncProcessId = uuidv4();
                    const syncMenuRequest: SyncMenusRequest = {
                        menuConfiguration: {
                            primaryLocation: {
                                manageMenuApplicationId: manageMenuAppId,
                                manageMenuApplicationName: `${locationAppFullName} - Manage Menu`,
                                liveApplicationId: locationAppId,
                                liveApplicationName: locationAppFullName,
                            }
                        },
                        orgId: props.orgContainer.state.currentOrganization.id,
                        processId: syncProcessId,
                    };
                    await syncMenus(syncMenuRequest);
                }

                props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'complete' as LocationSubStep }));
            };

            // ----------------------------------------------------------------
            // Helper: fully update an existing location app (mirrors create path)
            // ----------------------------------------------------------------
            const updateExistingLocationApp = async (existingApp: any, locationName: string, locationIndex: number) => {

                props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'updating-app' as LocationSubStep }));

                const locationAppId: string = existingApp.id;
                const locationAppFullName: string = locationName;

                const appliedTemplatesResponse = await getAppliedApplicationTemplatesForApp(locationAppId);
                if (hasDirectCoreAppliedTemplate(appliedTemplatesResponse.data ?? [], [aiAnsweringTemplateConfigId, foodOrderingModuleId, healthcareSchedulingModuleId])) {
                    props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'complete' as LocationSubStep }));
                    return;
                }

                const existingManagedAppliedTemplate = getManagedLocationAppliedTemplate(
                    appliedTemplatesResponse.data ?? [],
                    props.orgContainer.state.currentOrganization.id,
                    commonAppTemplateConfigId
                );
                if (!existingManagedAppliedTemplate) {
                    console.warn(`Skipping update for "${locationName}": no org-owned applied template found.`);
                    props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'complete' as LocationSubStep }));
                    return;
                }

                // Fetch GenAI flag for AI Ordering
                let genAIFoodOrderingFlag: any = undefined;
                if (values.assistantType === 'ai-ordering') {
                    try {
                        const allFeatureFlagsResponse = await getFeatureFlags();
                        genAIFoodOrderingFlag = allFeatureFlagsResponse.data?.find((f: any) => f.id === GenAIFoodOrdering);
                    } catch (e) {
                        // GenAI settings will not be applied if feature flags cannot be fetched
                    }
                }

                const buildGenAISettings = (flag: any) =>
                    (flag.featureFlagSettings ?? []).map((setting: any) => {
                        if (setting.id === FoodOrderingGenAIModelSettingId) {
                            return { ...setting, value: 'gemini-2.5-flash' };
                        }
                        if (setting.id === FoodOrderingGenAIPromptOverrideSettingId) {
                            return { ...setting, value: '{{constant.base_ordering_prompt}}' };
                        }
                        if (setting.name === 'Generative Assistant Ordering') {
                            return { ...setting, value: 'true' };
                        }
                        if (setting.name === 'Disable Legacy Checkout Flow') {
                            return { ...setting, value: 'true' };
                        }
                        return setting;
                    });

                const callDirection = values.callDirections?.[locationIndex] ?? 'inbound';
                const hasOutbound = callDirection === 'outbound' || callDirection === 'inbound-outbound';
                const storeId = values.assistantType === 'ai-ordering'
                    ? (values.storeIds?.[locationIndex] ?? '')
                    : undefined;

                // Fetch outbound feature flag for dental scheduling with outbound
                let outboundFeatureFlag: any = undefined;
                if (values.assistantType === 'dental-scheduling' && hasOutbound) {
                    try {
                        const allFeatureFlagsResponse = await getFeatureFlags();
                        outboundFeatureFlag = allFeatureFlagsResponse.data?.find((f: any) => f.id === OutboundCallFeatureFlagId);
                    } catch (e) {
                        // Outbound settings will not be applied if feature flags cannot be fetched
                    }
                }

                // ----------------------------------------------------------------
                // AI Ordering: handle Manage Menu app (update if exists, create if not)
                // ----------------------------------------------------------------
                let manageMenuAppId: string | undefined;
                if (values.assistantType === 'ai-ordering') {
                    const manageMenuAppName = `${locationAppFullName} - Manage Menu`;
                    const existingManageMenuApp = (props.manageMenuApps ?? []).find((app: any) => app.name === manageMenuAppName);
                    props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'create-manage-menu' as LocationSubStep }));

                    if (existingManageMenuApp) {
                        manageMenuAppId = existingManageMenuApp.id;
                        // Update existing Manage Menu app with latest POS settings + GenAI flag
                        await updateApplication(manageMenuAppId, {
                            name: existingManageMenuApp.name,
                            shortDescription: existingManageMenuApp.shortDescription,
                            description: existingManageMenuApp.description,
                            keywords: existingManageMenuApp.keywords,
                            invocationPhrase: existingManageMenuApp.invocationPhrase,
                            imageItemId: existingManageMenuApp.imageItemId,
                            featureFlags: [{ id: FoodOrderingFeatureFlagId }],
                            posName: values.posNameOrdering,
                            posLocationId: storeId
                        });
                        if (genAIFoodOrderingFlag) {
                            await updateApplicationFeatureFlagSettings(manageMenuAppId, {
                                featureFlagId: GenAIFoodOrdering,
                                featureFlagSettings: buildGenAISettings(genAIFoodOrderingFlag)
                            });
                        }
                    } else {
                        // Create new Manage Menu app
                        const manageMenuAppResponse = await createEmptyApplication(props.orgContainer.state.currentOrganization.id, {
                            name: manageMenuAppName,
                            shortDescription: manageMenuAppName,
                            description: manageMenuAppName,
                            invocationPhrase: manageMenuAppName
                        });
                        if (manageMenuAppResponse.resultType !== 'Ok' || !manageMenuAppResponse.data?.id) {
                            throw new Error(`Failed to create Manage Menu app for "${locationName}": ${manageMenuAppResponse.errors?.join(', ') || manageMenuAppResponse.resultType}`);
                        }
                        manageMenuAppId = manageMenuAppResponse.data.id;
                        await updateApplication(manageMenuAppId, {
                            name: manageMenuAppResponse.data.name,
                            shortDescription: manageMenuAppResponse.data.shortDescription,
                            description: manageMenuAppResponse.data.description,
                            keywords: manageMenuAppResponse.data.keywords,
                            invocationPhrase: manageMenuAppResponse.data.invocationPhrase,
                            imageItemId: manageMenuAppResponse.data.imageItemId,
                            featureFlags: [{ id: FoodOrderingFeatureFlagId }],
                            posName: values.posNameOrdering,
                            posLocationId: storeId
                        });
                        if (genAIFoodOrderingFlag) {
                            await updateApplicationFeatureFlagSettings(manageMenuAppId, {
                                featureFlagId: GenAIFoodOrdering,
                                featureFlagSettings: buildGenAISettings(genAIFoodOrderingFlag)
                            });
                        }
                    }
                }

                // For dental-scheduling, always PUT the location app to keep the outbound flag in sync
                // with the selected call direction. This ensures that switching from outbound to
                // inbound-only removes OutboundCallFeatureFlagId rather than leaving it enabled.
                if (values.assistantType === 'dental-scheduling') {
                    let featureFlagsForUpdate: { id: string; name?: string; featureFlagSettings?: any[] }[];
                    if (hasOutbound && outboundFeatureFlag) {
                        // Merge the outbound flag into the existing flags rather than replacing them wholesale.
                        featureFlagsForUpdate = mergeOutboundFeatureFlag(
                            existingApp.featureFlags ?? [],
                            OutboundCallFeatureFlagId,
                            outboundFeatureFlag
                        );
                    } else {
                        // Inbound-only: strip the outbound flag from existing flags while preserving others.
                        featureFlagsForUpdate = (existingApp.featureFlags ?? []).filter(
                            (f: any) => f.id !== OutboundCallFeatureFlagId
                        );
                    }
                    await updateApplication(locationAppId, {
                        name: existingApp.name,
                        shortDescription: existingApp.shortDescription,
                        description: existingApp.description,
                        keywords: existingApp.keywords,
                        invocationPhrase: existingApp.invocationPhrase,
                        imageItemId: existingApp.imageItemId,
                        timeZone: existingApp.timeZone,
                        featureFlags: featureFlagsForUpdate,
                        pmsName: values.pmsName,
                        pmsLocationId: values.storeIds?.[locationIndex] ?? undefined,
                        ...(hasOutbound && outboundFeatureFlag && {
                            OutboundCallIntegrationName: values.pmsName,
                            OutboundCallLocationId: values.storeIds?.[locationIndex] || ""
                        })
                    });

                    // Update ApplicationEnvironment: add outbound flag when outbound is selected,
                    // or remove it when switching to inbound-only.
                    const environmentsResponse = await getApplicationEnvironments(locationAppId);
                    const productionEnv = environmentsResponse.data?.find((env: any) => env.name === 'Production');
                    if (productionEnv) {
                        const existingEnvFlagsWithoutOutbound = (productionEnv.featureFlags ?? []).filter(
                            (f: any) => f.id !== OutboundCallFeatureFlagId
                        );
                        const envFeatureFlagsForUpdate = hasOutbound && outboundFeatureFlag
                            ? [
                                ...existingEnvFlagsWithoutOutbound,
                                {
                                    id: OutboundCallFeatureFlagId,
                                    name: outboundFeatureFlag.name,
                                    featureFlagSettings: outboundFeatureFlag.featureFlagSettings
                                }
                              ]
                            : existingEnvFlagsWithoutOutbound;
                        await updateEnvironmentSettings(productionEnv.id, {
                            name: productionEnv.name,
                            applicationName: existingApp.name,
                            featureFlags: envFeatureFlagsForUpdate
                        });
                    }
                }

                // Re-apply GenAI settings
                if (values.assistantType === 'ai-ordering' && genAIFoodOrderingFlag) {
                    await updateApplicationFeatureFlagSettings(locationAppId, {
                        featureFlagId: GenAIFoodOrdering,
                        featureFlagSettings: buildGenAISettings(genAIFoodOrderingFlag)
                    });
                }

                props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'applying-template' as LocationSubStep }));

                // Re-apply template values — fetch applied templates and push latest config to live app
                const orgTemplateConfigsForLocation = await getOrganizationTemplateConfigurations(props.orgContainer.state.currentOrganization.id);
                const orgTemplateConfigForLocation = orgTemplateConfigsForLocation.data?.find((config: any) => config.id === commonAppTemplateConfigId);

                const managedAppliedTemplate = getManagedLocationAppliedTemplate(
                    appliedTemplatesResponse.data ?? [],
                    props.orgContainer.state.currentOrganization.id,
                    commonAppTemplateConfigId
                );

                if (managedAppliedTemplate && orgTemplateConfigForLocation) {
                    const defaultConfig = getDefaultAssistantConfig(
                        values.assistantType,
                        values.pmsName || values.posNameOrdering,
                        locationName,
                        locationAppId,
                        existingApp.secret ?? "",
                        values.assistantType === 'ai-ordering' ? (values.storeIds?.[locationIndex] ?? '') : undefined
                    );

                    for (const appliedTemplate of [managedAppliedTemplate]) {
                        const existingFieldValues = appliedTemplate.templateFormFieldValues ?? [];

                        const templateSections = managedAppliedTemplate.templateForm?.templateFormSections?.length
                            ? managedAppliedTemplate.templateForm.templateFormSections
                            : orgTemplateConfigForLocation.templateForm.templateFormSections;
                        const templateFieldValues: TemplateFormFieldValueModel[] = [];
                        for (const section of templateSections) {
                            for (const formField of section.templateFormFields) {
                                const fieldTitle = formField.title;

                                const existingFieldValue = findFieldValueForField(formField, existingFieldValues);

                                const isFieldSelected = values.selectedFormFields.some(
                                    (selectedField: any) => fieldsMatch(selectedField, formField)
                                );
                                const isFieldDeselected = values.deselectedFormFields.some(
                                    (deselectedField: any) => fieldsMatch(deselectedField, formField)
                                );

                                const fieldValue = resolveLocationFieldValue({
                                    formField,
                                    isFieldSelected,
                                    isFieldDeselected,
                                    existingValue: existingFieldValue?.value,
                                    defaultConfig,
                                });

                                templateFieldValues.push({
                                    value: fieldValue,
                                    fieldType: formField.fieldType,
                                    tip: formField.tip,
                                    title: formField.title,
                                    label: formField.label,
                                    defaultValue: formField.defaultValue,
                                    associatedVariable: formField.associatedVariable,
                                    placeholder: formField.placeholder,
                                    templateFormFieldId: formField.id
                                });
                            }
                        }

                        const updateRequest: UpdateAppliedApplicationTemplateFormRequest = {
                            templateFormFieldValues: templateFieldValues,
                            dynamicFormSections: appliedTemplate.dynamicFormSections,
                            name: appliedTemplate.name,
                        };
                        await updateAppliedApplicationTemplate(appliedTemplate.id, updateRequest);
                    }
                } else if (appliedTemplatesResponse.data?.length > 0 && !orgTemplateConfigForLocation) {
                    // Applied templates exist but the org template config can't be matched to the current
                    // shared configuration app. This happens when a location app is linked to a different
                    // template config than the one currently in use. Skip the field update entirely to
                    // prevent wiping the location app's existing values.
                    console.warn(`Skipping field update for "${locationName}": org template config not found (commonAppTemplateConfigId: ${commonAppTemplateConfigId}). The location app may be linked to a different template config.`);
                } else if (appliedTemplatesResponse.data?.length > 0 && !managedAppliedTemplate) {
                    console.warn(`Skipping field update for "${locationName}": no org-owned applied template found.`);
                } else if (!appliedTemplatesResponse.data?.length) {
                    // No applied template yet — add one (handles edge case where template wasn't applied on creation)
                    const appliedTemplateResponse = await addAppliedApplicationTemplate(locationAppId, commonAppTemplateConfigId);
                    const refreshedOrgConfigs = await getOrganizationTemplateConfigurations(props.orgContainer.state.currentOrganization.id);
                    const refreshedConfig = refreshedOrgConfigs.data?.find((config: any) => config.id === commonAppTemplateConfigId);

                    if (refreshedConfig) {
                        const defaultConfig = getDefaultAssistantConfig(
                            values.assistantType,
                            values.pmsName || values.posNameOrdering,
                            locationName,
                            locationAppId,
                            existingApp.secret ?? "",
                            values.assistantType === 'ai-ordering' ? (values.storeIds?.[locationIndex] ?? '') : undefined
                        );

                        const templateFieldValues: TemplateFormFieldValueModel[] = [];
                        for (const section of refreshedConfig.templateForm.templateFormSections) {
                            for (const formField of section.templateFormFields) {
                                const fieldTitle = formField.title;
                                let fieldValue = formField.defaultValue;
                                const isFieldSelected = values.selectedFormFields.some(
                                    (selectedField: any) => fieldsMatch(selectedField, formField)
                                );
                                const configKey = formField.associatedVariable && defaultConfig[formField.associatedVariable] !== undefined
                                    ? formField.associatedVariable
                                    : fieldTitle;
                                if (isFieldSelected && defaultConfig[configKey] !== undefined) {
                                    const configValue = defaultConfig[configKey];
                                    if (configValue !== '' && configValue !== null && configValue !== undefined) {
                                        fieldValue = typeof configValue === 'string' ? configValue : String(configValue);
                                    }
                                }
                                templateFieldValues.push({
                                    value: fieldValue,
                                    fieldType: formField.fieldType,
                                    tip: formField.tip,
                                    title: formField.title,
                                    label: formField.label,
                                    defaultValue: formField.defaultValue,
                                    associatedVariable: formField.associatedVariable,
                                    placeholder: formField.placeholder,
                                    templateFormFieldId: formField.id
                                });
                            }
                        }
                        const updateRequest: UpdateAppliedApplicationTemplateFormRequest = {
                            templateFormFieldValues: templateFieldValues,
                            dynamicFormSections: appliedTemplateResponse.data.dynamicFormSections,
                            name: appliedTemplateResponse.data.name,
                        };
                        await updateAppliedApplicationTemplate(appliedTemplateResponse.data.id, updateRequest);
                    }
                }

                // ----------------------------------------------------------------
                // AI Ordering: Pull Latest Menu from POS, then Train and Publish
                // ----------------------------------------------------------------
                if (values.assistantType === 'ai-ordering' && manageMenuAppId) {
                    const locationStoreId = values.storeIds?.[locationIndex] ?? '';

                    props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'pull-menu' as LocationSubStep }));
                    const pullMenuRequest: UpdateMenuRequest = {
                        locationId: locationStoreId,
                        posName: values.posNameOrdering as POSNames,
                        manageMenuApplicationId: manageMenuAppId,
                        liveApplicationId: locationAppId,
                        updateLiveApplication: false,
                        skipEntityExapnsion: true,
                        skipMenuAugmentation: true,
                        orgId: props.orgContainer.state.currentOrganization.id,
                    };
                    await initiateMenuUpdate(pullMenuRequest);

                    props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'sync-module' as LocationSubStep }));
                    const syncProcessId = uuidv4();
                    const syncMenuRequest: SyncMenusRequest = {
                        menuConfiguration: {
                            primaryLocation: {
                                manageMenuApplicationId: manageMenuAppId,
                                manageMenuApplicationName: `${locationAppFullName} - Manage Menu`,
                                liveApplicationId: locationAppId,
                                liveApplicationName: locationAppFullName,
                            }
                        },
                        orgId: props.orgContainer.state.currentOrganization.id,
                        processId: syncProcessId,
                    };
                    await syncMenus(syncMenuRequest);
                }

                props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'complete' as LocationSubStep }));
            };

            // create or update common app
            if (!props.commonApp) {
                await createCommonAppAndAppliedAppTemplate();
            } else {
                await updateCommonAppAndAppliedAppTemplate();
            };

            props.setProgressStep?.('configure-template');
            props.setProgressLocationName?.(getCommonAppNameForAssistantType(values.assistantType));
            const orgTemplateConfigsResponse = await getOrganizationTemplateConfigurations(props.orgContainer.state.currentOrganization.id);
            let orgTemplateConfig = orgTemplateConfigsResponse.data[0];
            if (orgTemplateConfigsResponse.data.length > 1) {
                const customModuleName = getCustomModuleName(values.assistantType);
                orgTemplateConfig = orgTemplateConfigsResponse.data.find(templateConfig => templateConfig.name === customModuleName);

                // Fallback to the old AI answering or dental scheduling module names if no match found
                if (!orgTemplateConfig) {
                    orgTemplateConfig = orgTemplateConfigsResponse.data.find(templateConfig =>
                        templateConfig.name === aiAnsweringCustomModuleName ||
                        templateConfig.name === aiDentalSchedulingCustomModuleName);
                }

                // Final fallback to first available
                if (!orgTemplateConfig) {
                    orgTemplateConfig = orgTemplateConfigsResponse.data[0];
                }
            }

            if (!orgTemplateConfig) {
                await createCommonAppTemplateConfiguration()
            } else {
                await updateCommonAppTemplateConfiguration()
            };

            // create or update location apps
            props.setProgressStep?.('create-locations');

            // Initialize all location rows to 'pending' so they appear in the stepper immediately
            const activeLocationNames = values.locationNames.filter(n => n?.trim());
            props.setLocationProgress?.(
                Object.fromEntries(activeLocationNames.map(n => [n, 'pending' as LocationSubStep]))
            );

            for (let locationIndex = 0; locationIndex < values.locationNames.length; locationIndex++) {
                const locationName = values.locationNames[locationIndex];
                if (!locationName?.trim()) continue;
                const existingApp = props.locationApps?.find(app => app.name === locationName);
                if (existingApp) {
                    await updateExistingLocationApp(existingApp, locationName, locationIndex);
                    updatedNames.push(locationName);
                } else {
                    props.setProgressLocationName?.(locationName);
                    await createLocationAppAndApplyAppTemplate(locationName, locationIndex);
                    createdNames.push(locationName);
                }
            };

            props.setProgressStep?.('publishing');
            props.setProgressLocationName?.('');
            props.setIsSubmittingForm(false);

            // Refresh the org applications list in the container so the dashboard home
            // immediately shows the new/updated location on navigation without requiring
            // a manual page refresh. This updates both orgApplicationsList and
            // applicationsCount, which invalidates the cache used by ApplicationDashboard
            // and re-triggers OrganizationDashboardContent's loadTemplates effect.
            await props.appContainer.searchForOrgApplications(
                props.orgContainer.state.currentOrganization.id,
                1,
                30,
                undefined,
                undefined,
                true
            );

            // Build differentiated success message
            const successParts: string[] = [];
            successParts.push(commonAppWasCreated ? 'Shared configuration app was created.' : 'Shared configuration app was updated.');
            if (createdNames.length > 0) {
                successParts.push(createdNames.length > 5
                    ? `Created ${createdNames.length} new location apps.`
                    : `Created ${createdNames.length} new location app${createdNames.length > 1 ? 's' : ''}: ${createdNames.join(', ')}.`);
            }
            if (updatedNames.length > 0) {
                successParts.push(updatedNames.length > 5
                    ? `Updated ${updatedNames.length} existing location apps.`
                    : `Updated ${updatedNames.length} existing location app${updatedNames.length > 1 ? 's' : ''}: ${updatedNames.join(', ')}.`);
            }
            setStatus({
                type: 'success',
                message: successParts.join(' '),
                details: []
            });

        } catch (error) {
            props.setIsSubmittingForm(false);
            props.setProgressStep?.(null);
            props.setProgressMode?.(null);
            props.setLocationProgress?.(() => ({}));

            // Set error status for the form to display
            const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred during form submission.';
            setStatus({
                type: 'error',
                message: errorMessage,
                details: []
            });
        }
    }
})(SparkLocationsForm);

export default ManageSparkLocationsForm;
